import { RefObject, useCallback, useEffect, useState } from 'react';
import { throttle } from 'underscore';

type TProps = {
  ref: RefObject<HTMLElement | null>;
  disabled?: boolean;
  dragHandleId?: string;
  throttleTime?: number;
};

const useDraggableElement = ({ ref, disabled, dragHandleId, throttleTime }: TProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [offset, setOffset] = useState({ x: 50, y: 50 });

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      if (ref.current) {
        if (dragHandleId && event?.target instanceof HTMLElement && event.target.id !== dragHandleId) {
          return;
        }
        const rect = ref.current.getBoundingClientRect();
        setOffset({ x: event.clientX - rect.left, y: event.clientY - rect.top });
        setIsDragging(true);
        event.preventDefault(); // Prevent default behavior to avoid text selection
      }
    },
    [dragHandleId, ref]
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isDragging) {
        setPosition({ x: event.clientX - offset.x, y: event.clientY - offset.y });
      }
    },
    [isDragging, offset]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    const element = ref.current;

    const throttledMove = throttleTime ? throttle(handleMouseMove, throttleTime) : handleMouseMove;

    if (!disabled && element) {
      if (isDragging) {
        window.addEventListener('mousemove', throttledMove);
        window.addEventListener('mouseup', handleMouseUp);
      } else {
        window.removeEventListener('mousemove', throttledMove);
        window.removeEventListener('mouseup', handleMouseUp);
      }

      element.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      window.removeEventListener('mousemove', throttledMove);
      window.removeEventListener('mouseup', handleMouseUp);
      element?.removeEventListener('mousedown', handleMouseDown);
    };
  }, [disabled, handleMouseDown, handleMouseMove, handleMouseUp, isDragging, offset, ref, throttleTime]);

  useEffect(() => {
    const el = ref.current;
    if (!position || !el) return;
    if (isDragging) {
      // transform works well, except for first click... so opting for left/top instead
      el.style.transform = `translate(${position.x}px, ${position.y}px)`;
      el.style.cursor = 'grabbing';
    } else {
      el.style.cursor = 'grab';
    }
  }, [isDragging, position, ref]);
};

export default useDraggableElement;
